import { PRODUCTS_AND_SERVICES } from 'services/constants';
import { sendRedistributeDocumentsToApi } from 'services/documents/sendRedistributeDocumentsToApi';
import { sendBulkDocumentsToApi } from 'services/documents/sendBulkDocumentsToApi';
import { getDocumentsToDistributeApi } from 'services/documents/getDocumentsToDistributeApi';
import { distributeDocumentsToApi } from 'services/documents/distributeDocumentsToApi';
import { sendDocumentToApi } from 'services/documents/sendDocumentToApi';
import { AppDispatch, RootState } from 'store';
import { getServiceAgreement } from './accountServiceAgreementsThunk';
import { moveForwardServiceAgreementProcessApi } from 'services/service-agreement/moveForwardServiceAgreementProcessApi';
import { setLoader } from 'reducers/appConfigReducer';
import { setDocumentsToDistribute } from 'reducers/productDocumentReducer';
import { ItemForDistribution } from 'components/productsAndSerevices/ProductDocumentsToDistributeDialog';
import { getDocumentBinaryFromApi } from 'services/documents/getDocumentBinaryFromApi';
import { closeModal, openModal } from 'reducers/modalReducer';

export const sendDocument =
  (
    serviceAgreementId: string,
    file: File,
    taskId: string,
    distributionAssignee: string,
    languange: string,
    formCode: string,
    checkBoxSelection?: string[],
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Sending Document...',
      }),
    );

    try {
      const sendDocumentToApiResult = await sendDocumentToApi(serviceAgreementId, file, languange, formCode);
      if (sendDocumentToApiResult?.status !== 200) {
        return;
      }

      const brUid = getState().account.brUid;

      const stringifiedFormData = JSON.stringify({
        userName: distributionAssignee,
        fileName: file.name,
        languange: languange,
        formCode: formCode,
        checkBoxText: checkBoxSelection,
      });

      await moveForwardServiceAgreementProcessApi(
        taskId,
        serviceAgreementId,
        stringifiedFormData,
        undefined,
        distributionAssignee,
      );

      dispatch(getServiceAgreement(serviceAgreementId, null, true));
      if (brUid) {
        dispatch(fetchDocumentsToDistribute(brUid, null));
      }
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
    } catch (error: unknown) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when distribution documents',
              userErrorMessage: 'An error occurred when distribution documents. Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
    }
  };

export const getDocument =
  (serviceAgreementId: string, documentUid: string, documentName: string) => async (dispatch: AppDispatch) => {
    getDocumentBinaryFromApi(serviceAgreementId, documentUid)
      .then((res) => {
        downloadFileFromBlob(res, documentName);
      })
      .catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when retrieving document content',
                userErrorMessage: 'An error occurred when retrieving document content. Please try again later.',
              },
            },
          }),
        );
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
      });
  };

function downloadFileFromBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export const distributeDocuments =
  (taskId: string, language: string, documentUids: string[], CLM_URL: string, serviceAgreementId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Distributing Documents...',
      }),
    );
    const brUid = getState().account.brUid;
    distributeDocumentsToApi(taskId, language, documentUids, serviceAgreementId)
      .then((res) => {
        dispatch(getServiceAgreement(serviceAgreementId, null, true));
        if (brUid) {
          dispatch(fetchDocumentsToDistribute(brUid, null));
        }
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );

        window.parent.postMessage(
          {
            message: 'redirectDocumentTab_V2',
            value: {
              distributionRequestId: res?.requestId,
              initiator: PRODUCTS_AND_SERVICES,
            },
          },
          CLM_URL,
        );
      })
      .catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when distributing documents',
                userErrorMessage: 'An error occurred when distributing documents. Please try again later.',
              },
            },
          }),
        );
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
      });
  };

export const fetchDocumentsToDistribute =
  (brId: string | null, partnerId: string | null) => async (dispatch: AppDispatch) => {
    if (partnerId) {
      return;
    }
    if (!brId) {
      return;
    }
    const response = await getDocumentsToDistributeApi(brId).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when retrieving documents to distribute',
              userErrorMessage: 'An error occurred when retrieving documents to distribute. Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    });

    if (!response) {
      return;
    }

    dispatch(setDocumentsToDistribute(response));
  };

export const sendBulkDocuments =
  (items: ItemForDistribution[], brId: string, CLM_URL: string) => async (dispatch: AppDispatch) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Sending Bulk Documents...',
      }),
    );

    const response = await sendBulkDocumentsToApi(items, brId).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when distributing documents',
              userErrorMessage: 'An error occurred when distributing documents (bulk). Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    });

    dispatch(fetchDocumentsToDistribute(brId, null));

    if (!response) {
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    }

    window.parent.postMessage(
      {
        message: 'redirectDocumentTab_V2',
        value: {
          distributionRequestId: response?.requestId,
          initiator: PRODUCTS_AND_SERVICES,
        },
      },
      CLM_URL,
    );

    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
  };

export const sendRedistributeDocuments =
  (items: string[], brId: string | undefined) => async (dispatch: AppDispatch) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Sending Redistribute Documents...',
      }),
    );

    const response = await sendRedistributeDocumentsToApi(items, brId).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when redistributing documents',
              userErrorMessage: 'An error occurred when redistributing documents. Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    });
    if (response) {
      dispatch(getServiceAgreement(response, null, true));
    }
    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
  };
